// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// Per-mount metadata is a plain context value (it never changes after mount);
// the sidebar state is a keepAlive atom.
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { Atom } from 'effect/reactivity';
import { createContext, type PropsWithChildren, use, useState } from 'react';
import type { DocPackReadingClass } from '@listening/doc-pack';
import type { GoogleDocPublicPdfData } from '@listening/frontend/public-doc-catalog/types';
import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { PremiumDialogContext } from '@/components/web-fe/misc/providers/premium-dialog-provider';

export type ReadAlongContextStoreProps = {
  metadata?: GoogleDocPublicPdfData | undefined;
};

const ReadAlongStoreContext = createContext<ReadAlongContextStoreProps | null>(
  null,
);
export type ReadAlongTruncation = {
  convertedPageCount: number;
  totalPageCount: number;
};

type ReadAlongViewer = 'normal' | 'pdf';

type ReadAlongSurface = {
  surface: 'default' | 'public-tool';
  truncation?: ReadAlongTruncation | undefined;
  viewer?: {
    readonly value: ReadAlongViewer | undefined;
    readonly set: (viewer: ReadAlongViewer) => void;
  };
};

const ReadAlongSurfaceContext = createContext<ReadAlongSurface>({
  surface: 'default',
});

/**
 * A public-tool page keeps its view choice to itself: it opens in
 * `initialViewer` and switching views never touches the visitor's saved
 * preference, which their own library reads.
 */
export function ReadAlongSurfaceProvider({
  children,
  surface,
  truncation,
  initialViewer,
}: PropsWithChildren<{
  surface: 'public-tool';
  truncation?: ReadAlongTruncation | undefined;
  initialViewer?: ReadAlongViewer | undefined;
}>) {
  const [chosenViewer, setChosenViewer] = useState<ReadAlongViewer>();
  return (
    <ReadAlongSurfaceContext
      value={{
        surface,
        truncation,
        viewer: { value: chosenViewer ?? initialViewer, set: setChosenViewer },
      }}
    >
      {children}
    </ReadAlongSurfaceContext>
  );
}

export function ReadAlongStoreProvider({
  children,
  ...props
}: Partial<ReadAlongContextStoreProps> & PropsWithChildren) {
  // Snapshot the initial props for the mount lifetime — the zustand store
  // this replaces was never written to after creation.
  const [value] = useState<ReadAlongContextStoreProps>(() => ({ ...props }));
  return (
    <ReadAlongStoreContext value={value}>{children}</ReadAlongStoreContext>
  );
}

export function useReadAlongContext<T>(
  selector: (state: ReadAlongContextStoreProps) => T,
): T {
  const state = use(ReadAlongStoreContext);
  if (!state) throw new Error('Missing ReadAlongContext.Provider in the tree');
  return selector(state);
}

export function useReadAlongPageType() {
  const { surface } = use(ReadAlongSurfaceContext);
  // const matchRoute = useMatchRoute();
  // const isPublicDoc = !!matchRoute({
  //   to: '/doc/$docId',
  // });
  // const isWebAppDoc = !isPublicDoc;
  // return { isPublicDoc, isWebAppDoc };
  const premiumDialog = use(PremiumDialogContext);
  const isWebAppDoc = !!premiumDialog;
  const isPublicDoc = !isWebAppDoc;
  return {
    isPublicDoc,
    isPublicTool: surface === 'public-tool',
    isWebAppDoc,
  };
}

export function useReadAlongSurface() {
  return use(ReadAlongSurfaceContext);
}

/** The reader view: the page's own on a public tool, else the saved preference. */
export function useReadAlongViewer() {
  const { viewer } = use(ReadAlongSurfaceContext);
  const savedViewer = useAudioPrefsStore((state) => state.readAlongViewer);
  const setSavedViewer = useAudioPrefsStore(
    (state) => state.setReadAlongViewer,
  );
  return viewer === undefined
    ? { value: savedViewer, set: setSavedViewer }
    : { value: viewer.value ?? savedViewer, set: viewer.set };
}

export type SidebarState =
  | 'closed'
  | 'sections'
  | 'notes'
  | 'information'
  | 'reading';

const readAlongSidebarStateAtom = Atom.make<SidebarState>('closed').pipe(
  Atom.keepAlive,
);

export function useReadAlongSidebarStore() {
  const sidebarState = useAtomValue(readAlongSidebarStateAtom);
  const setSidebarState = useAtomSet(readAlongSidebarStateAtom);
  return { sidebarState, setSidebarState };
}

const hoveredReadingLabelAtom = Atom.make<DocPackReadingClass | null>(
  null,
).pipe(Atom.keepAlive);

export const useHoveredReadingLabel = () =>
  useAtomValue(hoveredReadingLabelAtom);

export const useSetHoveredReadingLabel = () =>
  useAtomSet(hoveredReadingLabelAtom);
